/**
 * Income Tax Calculator API Service
 * Handles communication with the backend income tax endpoints for Canada and US
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

// ============ CANADA TYPES ============

export interface CanadaTaxCalculationRequest {
  country: 'CA';
  employment_income: number;
  self_employment_income: number;
  other_income: number;
  capital_gains: number;
  eligible_dividends: number;
  rrsp_contribution: number;
  charitable_donations?: number;
  province: string;
  age: number;
  year: number;
}

export interface TaxBracket {
  min: number;
  max: number | null;
  rate: number;
  taxable: number;
  tax: number;
}

export interface CanadaTaxCalculationResponse {
  summary: {
    gross_income: number;
    taxable_income: number;
    total_deductions: number;
    net_income: number;
    effective_tax_rate: number;
    total_effective_rate: number;
  };
  cpp: {
    employment: {
      cpp_base: number;
      cpp2: number;
      total: number;
      max_cpp_base: number;
      max_cpp2: number;
    };
    self_employment: {
      cpp_base: number;
      cpp2: number;
      total: number;
      max_cpp_base: number;
      max_cpp2: number;
    };
    total: number;
  };
  ei: {
    premium: number;
    max_premium: number;
  };
  federal_tax: {
    gross: number;
    credits: number;
    dividend_credit: number;
    net: number;
    brackets: TaxBracket[];
  };
  provincial_tax: {
    province: string;
    gross: number;
    credits: number;
    dividend_credit: number;
    net: number;
    brackets: TaxBracket[];
  };
  total_income_tax: number;
  marginal_rates: {
    federal: number;
    provincial: number;
    combined: number;
  };
  donation_credit?: {
    federal_credit: number;
    provincial_credit: number;
    total_credit: number;
    eligible_amount: number;
  };
  deductions_used: {
    rrsp: number;
    charitable_donations?: number;
  };
  year: number;
}

// ============ USA TYPES ============

export interface USATaxCalculationRequest {
  country: 'US';
  employment_income: number;
  self_employment_income: number;
  other_income: number;
  capital_gains: number;
  qualified_dividends: number;
  retirement_contribution: number;
  state: string;
  filing_status: 'single' | 'married';
  year: number;
}

export interface USATaxCalculationResponse {
  country: 'US';
  summary: {
    gross_income: number;
    taxable_income: number;
    ordinary_income: number;
    preferential_income: number;
    total_deductions: number;
    net_income: number;
    effective_tax_rate: number;
    total_effective_rate: number;
  };
  fica: {
    employment: {
      social_security: number;
      medicare: number;
      additional_medicare: number;
      total: number;
      max_social_security: number;
    };
    self_employment: {
      social_security: number;
      medicare: number;
      additional_medicare: number;
      total: number;
      max_social_security: number;
    };
    total: number;
    social_security: number;
    medicare: number;
  };
  federal_tax: {
    ordinary_tax: number;
    capital_gains_tax: number;
    capital_gains_rate: number;
    niit: number;
    niit_applies: boolean;
    gross: number;
    standard_deduction: number;
    net: number;
    brackets: TaxBracket[];
  };
  state_tax: {
    state: string;
    gross: number;
    net: number;
    brackets: TaxBracket[];
    note?: string;
  };
  total_income_tax: number;
  marginal_rates: {
    federal: number;
    federal_cap_gains: number;
    state: number;
    combined: number;
  };
  deductions_used: {
    standard_deduction: number;
    retirement_contribution: number;
    self_employment_deduction: number;
  };
  filing_status: string;
  year: number;
}

// ============ UNIFIED TYPES ============

export type TaxCalculationRequest = CanadaTaxCalculationRequest | USATaxCalculationRequest;
export type TaxCalculationResponse = CanadaTaxCalculationResponse | USATaxCalculationResponse;

// Type guard to check if response is Canadian
export function isCanadaResponse(response: TaxCalculationResponse): response is CanadaTaxCalculationResponse {
  return !('country' in response) || response.country !== 'US';
}

// Type guard to check if response is US
export function isUSAResponse(response: TaxCalculationResponse): response is USATaxCalculationResponse {
  return 'country' in response && response.country === 'US';
}

export interface RRSPImpactScenario {
  rrsp_amount: number;
  federal_tax: number;
  provincial_tax: number;
  total_deductions: number;
  net_income: number;
  tax_savings: number;
}

export interface RRSPImpactResponse {
  scenarios: RRSPImpactScenario[];
  province: string;
  year: number;
}

export interface Province {
  code: string;
  name: string;
}

export interface State {
  code: string;
  name: string;
  note?: string;
}

class IncomeTaxAPI {
  private async fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}/backend/income-tax${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }

    return data.data;
  }

  async calculateTax(request: TaxCalculationRequest): Promise<TaxCalculationResponse> {
    return this.fetchAPI<TaxCalculationResponse>('/calculate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async calculateCanadaTax(request: Omit<CanadaTaxCalculationRequest, 'country'>): Promise<CanadaTaxCalculationResponse> {
    return this.fetchAPI<CanadaTaxCalculationResponse>('/calculate', {
      method: 'POST',
      body: JSON.stringify({ ...request, country: 'CA' }),
    });
  }

  async calculateUSATax(request: Omit<USATaxCalculationRequest, 'country'>): Promise<USATaxCalculationResponse> {
    return this.fetchAPI<USATaxCalculationResponse>('/calculate', {
      method: 'POST',
      body: JSON.stringify({ ...request, country: 'US' }),
    });
  }

  async calculateCPP(employmentIncome: number, year: number, selfEmployed: boolean): Promise<{
    cpp_base: number;
    cpp2: number;
    total: number;
    max_cpp_base: number;
    max_cpp2: number;
  }> {
    return this.fetchAPI('/cpp', {
      method: 'POST',
      body: JSON.stringify({
        employment_income: employmentIncome,
        year,
        self_employed: selfEmployed,
      }),
    });
  }

  async calculateEI(employmentIncome: number, year: number): Promise<{
    ei_premium: number;
    max_ei: number;
  }> {
    return this.fetchAPI('/ei', {
      method: 'POST',
      body: JSON.stringify({
        employment_income: employmentIncome,
        year,
      }),
    });
  }

  async getMarginalRate(taxableIncome: number, province: string): Promise<{
    federal: number;
    provincial: number;
    combined: number;
  }> {
    return this.fetchAPI('/marginal-rate', {
      method: 'POST',
      body: JSON.stringify({
        taxable_income: taxableIncome,
        province,
      }),
    });
  }

  async getRRSPImpact(request: Omit<CanadaTaxCalculationRequest, 'country'> & { rrsp_amounts?: number[] }): Promise<RRSPImpactResponse> {
    return this.fetchAPI<RRSPImpactResponse>('/rrsp-impact', {
      method: 'POST',
      body: JSON.stringify({ ...request, country: 'CA' }),
    });
  }

  async getProvinces(): Promise<Province[]> {
    return this.fetchAPI<Province[]>('/provinces');
  }

  async getStates(): Promise<State[]> {
    return this.fetchAPI<State[]>('/states');
  }
}

export const incomeTaxAPI = new IncomeTaxAPI();
export default incomeTaxAPI;
